import {
   ChangeDetectionStrategy,
   Component,
   EventEmitter,
   Input,
   OnChanges,
   OnDestroy,
   OnInit,
   Output,
   SimpleChanges,
} from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { LoadingState } from '@lcs/shared/api';
import { ReportParameterFormFactoryService } from '@ndt/reports/shared/report-parameter-list/services/report-parameter-form-factory.service';
import { ReportParameterFormService } from '@ndt/reports/shared/report-parameter-list/services/report-parameter-form.service';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TileSetType } from 'src/app/pages/dashboard/tile-set-type-select/models/tile-set.model';
import { DashboardTile } from '../../../dashboard/models/dashboard-tile.model';
import { TileParametersBase } from '../../models/tile-parameters.model';
import { tileSave } from '../../state/tile.actions';
import { getTileCallState } from '../../state/tiles.selectors';

@Component({
   changeDetection: ChangeDetectionStrategy.OnPush,
   selector: 'ndt-tile-parameters',
   templateUrl: './tile-parameters.component.html',
   standalone: false,
})
// eslint-disable-next-line @typescript-eslint/naming-convention
export class TileParametersComponent<P extends TileParametersBase> implements OnInit, OnChanges, OnDestroy {
   @Input() formService: ReportParameterFormService<P | any>;

   @Input() tileConfig: DashboardTile;

   @Output() remove = new EventEmitter<never>();

   form: UntypedFormGroup;

   loading: boolean;

   name: string = '';

   private destroyed$ = new Subject<void>();

   constructor(private store: Store, private formFactory: ReportParameterFormFactoryService) {}

   ngOnInit() {
      this.store
         .select(getTileCallState(this.tileConfig.dashboardTileId))
         .pipe(takeUntil(this.destroyed$))
         .subscribe((callState) => {
            this.loading = callState === LoadingState.Loading;
         });
      this.name = this.tileConfig.dashboardTileLayoutConfiguration.name || '';
   }

   ngOnChanges(changes: SimpleChanges): void {
      if (changes.formService && this.formService) {
         this.form = this.formService.form;
      }
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   onSubmit() {
      this.formService.form.updateValueAndValidity();
      if (this.formService.form.valid) {
         const parameters: P | null = this.formService.getValue(true);

         if (parameters) {
            this.store.dispatch(
               tileSave({
                  dashboardTileId: this.tileConfig.dashboardTileId,
                  tileId: this.tileConfig.tile.tileId,
                  parameters,
               })
            );
         }
      }
   }

   onTileSetTypeChange(tileSetType: TileSetType) {
      const preValues = this.form.get('values') as UntypedFormArray;
      const preValueControls = preValues?.controls ?? [];

      this.formService = this.formFactory.getService(tileSetType.tile.tileId);
      this.form = this.formService.form;

      const currValues = this.form.get('values') as UntypedFormArray;
      const currValueControls = currValues?.controls ?? [];

      preValueControls?.forEach((pc) => {
         const match = currValueControls.find((vc) => vc.value.parameterType === pc.value.parameterType);
         match?.patchValue(pc.value, { emitEvent: false });
         if (pc.dirty) match?.markAsDirty();
      });
   }
}
